import styled, { keyframes } from "styled-components";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const Container = styled.div`
  display: flex;
  min-height: 100vh;
  background: #f4f7f6;
  font-family: "Inter", sans-serif;

  /* Variáveis de Tema */
  --primary: ${(props) => props.theme.colors?.primary || "#10b981"};
  --primary-hover: ${(props) => props.theme.colors?.primaryHover || "#059669"};
  --secondary: ${(props) => props.theme.colors?.secondary || "#3b82f6"};
  --secondary-hover: ${(props) =>
    props.theme.colors?.secondaryHover || "#2563eb"};
  --background: ${(props) => props.theme.colors?.background || "#f4f7f6"};
  --surface: ${(props) => props.theme.colors?.surface || "#ffffff"};
  --text-main: ${(props) => props.theme.colors?.text || "#1a1a1a"};
  --text-secondary: ${(props) => props.theme.colors?.textSecondary || "#666"};
  --border: ${(props) => props.theme.colors?.border || "#e5e7eb"};

  --shadow-sm: 0 2px 10px rgba(0, 0, 0, 0.05);
  --shadow-md: 0 5px 15px rgba(0, 0, 0, 0.08);
  --radius-md: 12px;
  --radius-full: 9999px;

  /* RESPONSIVIDADE: Muda de linha para coluna em tablets/celulares */
  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

export const MainContent = styled.div`
  flex: 1;
  padding: 40px;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 20px;
  }

  header {
    background: var(--surface);
    padding: 20px;
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-sm);
    margin-bottom: 20px;

    .header-top {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;

      @media (max-width: 480px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
      }

      h2 {
        display: flex;
        align-items: center;
        gap: 10px;
        color: var(--text-main);
      }
    }
  }

  .order-type-toggle {
    display: flex;
    background: #f1f1f1;
    padding: 4px;
    border-radius: 8px;

    button {
      padding: 8px 16px;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      font-weight: 600;
      color: var(--text-secondary);
      transition: 0.2s;
      background: transparent;

      &.active {
        background: var(--primary);
        color: white;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }
    }
  }

  .customer-info-grid {
    display: grid;
    grid-template-columns: 2fr 1fr 1fr;
    gap: 15px;

    /* RESPONSIVIDADE: Inputs empilhados no mobile */
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
`;

export const InputGroup = styled.div`
  display: flex;
  align-items: center;
  background: #f9fafb;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 8px 12px;
  gap: 10px;

  input,
  select {
    border: none;
    background: transparent;
    width: 100%;
    outline: none;
    font-size: 14px;
    color: var(--text-main);
  }
`;

export const AddressSection = styled.div`
  margin-top: 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 15px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid var(--border);

  .address-row {
    display: flex;
    gap: 10px;

    @media (max-width: 600px) {
      flex-direction: column;
    }

    input {
      background: white;
      border: 1px solid var(--border);
      padding: 8px 12px;
      border-radius: 6px;
      outline: none;
      font-size: 13px;
    }

    .cep-input {
      width: 120px;
      @media (max-width: 600px) {
        width: 100%;
      }
    }
    .street-input {
      flex: 1;
    }
    .number-input {
      width: 80px;
      @media (max-width: 600px) {
        width: 100%;
      }
    }
  }
`;

export const CashSection = styled.div`
  margin-top: 15px;
  padding: 12px 15px;
  background: #fffbeb;
  border-radius: 8px;
  border: 1px solid #fde68a;
  display: flex;
  align-items: center;
  gap: 15px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
  }

  label {
    font-weight: 600;
    font-size: 14px;
    color: #92400e;
  }
  input {
    width: 100px;
    padding: 6px;
    border: 1px solid #fcd34d;
    border-radius: 4px;
    outline: none;
  }
  .change {
    margin-left: auto;
    font-size: 14px;
    color: #92400e;

    @media (max-width: 600px) {
      margin-left: 0;
    }

    strong {
      font-size: 16px;
    }
  }
`;

export const FilterBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 20px;

  .search-box {
    display: flex;
    align-items: center;
    background: var(--surface);
    padding: 12px;
    border-radius: 10px;
    gap: 10px;
    box-shadow: var(--shadow-sm);

    input {
      border: none;
      width: 100%;
      outline: none;
      font-size: 16px;
    }
  }

  .categories {
    display: flex;
    gap: 10px;
    overflow-x: auto;
    padding-bottom: 10px;

    /* Esconde barra de rolagem mas mantém funcionalidade */
    &::-webkit-scrollbar {
      height: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background: var(--border);
      border-radius: 10px;
    }

    button {
      white-space: nowrap;
      padding: 8px 20px;
      border-radius: var(--radius-full);
      border: 1px solid var(--border);
      background: var(--surface);
      cursor: pointer;
      font-size: 14px;
      transition: 0.2s;

      &.active {
        background: #333;
        color: white;
        border-color: #333;
      }
    }
  }
`;

export const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;

  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 10px;
  }
`;

export const ProductCard = styled.div`
  background: var(--surface);
  padding: 24px;
  border-radius: 20px;
  position: relative;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  border: 1px solid var(--border);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);

  @media (max-width: 480px) {
    padding: 16px;
    border-radius: 16px;
  }

  &:hover {
    transform: translateY(-8px);
    border-color: var(--primary);
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    
    .add {
      transform: scale(1.1);
      background: var(--primary-hover);
    }
  }

  h4 {
    margin: 0 0 6px;
    color: var(--text-main);
    font-size: 1.1rem;
    font-weight: 700;
    line-height: 1.2;
  }

  p {
    font-size: 13px;
    color: var(--text-secondary);
    margin-bottom: 20px;
    line-height: 1.5;
    height: 40px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

 .price-tag {
    font-weight: 800;
    color: var(--text-main);
    font-size: 22px;
    letter-spacing: -1px;
    display: flex;
    align-items: baseline;
    gap: 4px;
    
    &::before {
      content: 'R$:';
      font-size: 17px;
      font-weight: 600;
      color: var(--primary);
      text-transform: uppercase;
    }

    @media (max-width: 480px) {
      font-size: 18px;
      
      &::before {
        font-size: 12px;
      }
    }
  }

  .add {
    position: absolute;
    bottom: 20px;
    right: 20px;
    background: var(--primary);
    color: white;
    width: 36px;
    height: 36px;
    border-radius: 12px; /* Quadrado arredondado é mais moderno que círculo total */
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    box-shadow: 0 4px 10px rgba(16, 185, 129, 0.3); /* Sombra colorida */

    @media (max-width: 480px) {
      bottom: 16px;
      right: 16px;
      width: 32px;
      height: 32px;
    }
  }
`;

export const CartSidebar = styled.div`
  width: 380px;
  min-width: 380px;
  max-width: 380px;
  background: var(--surface);
  border-left: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 24px;
  height: 100vh;
  position: sticky;
  top: 0;
  box-shadow: var(--shadow-sm);
  z-index: 10;

  @media (max-width: 1024px) {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
    height: auto;
    min-height: 500px;
    position: relative;
    border-left: none;
    border-top: 1px solid var(--border);
    border-radius: 20px 20px 0 0;
    padding: 20px;
    margin-top: 20px;
  }
`;

export const CartList = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: 70vh;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border);
    border-radius: 10px;
  }

  @media (max-width: 1024px) {
    max-height: 50vh;
  }
`;
export const CartItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 35px 0;
  border-bottom: 1px dashed var(--border);

  .info {
    display: flex;
    flex-direction: column;
    strong {
      font-size: 14px;
      color: var(--text-main);
    }
    span {
      font-size: 13px;
      color: var(--primary);
      font-weight: 600;
    }
  }

  .controls {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #f8f9fa;
    padding: 5px 10px;
    border-radius: var(--radius-full);

    button {
      background: white;
      border: 1px solid var(--border);
      border-radius: 50%;
      width: 24px;
      height: 24px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
`;

export const CartFooter = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 2px solid var(--background);

  .total {
    display: flex;
    justify-content: space-between;
    font-size: 18px;
    margin-bottom: 20px;
    strong {
      color: var(--primary);
      font-size: 24px;
    }
  }

  .confirm {
    width: 100%;
    padding: 18px;
    border-radius: var(--radius-md);
    border: none;
    background: var(--primary);
    color: white;
    font-size: 18px;
    font-weight: 800;
    cursor: pointer;
    transition: 0.2s;

    &:disabled {
      background: #ccc;
      cursor: not-allowed;
    }
    &:hover:not(:disabled) {
      background: var(--primary-hover);
      transform: scale(1.02);
    }
  }
`;

export const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 50px;
  width: 100%;
  .spinner {
    animation: ${spin} 1s linear infinite;
    color: var(--primary);
    font-size: 30px;
  }
`;
export const ClearCartButton = styled.button`
  background: transparent;
  border: 1px solid transparent;
  color: #ef4444; /* Vermelho suave */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  
  /* Se você quiser um texto junto com o ícone, pode usar gap */
  gap: 5px;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    background: #fee2e2; /* Fundo vermelho bem clarinho no hover */
    color: #b91c1c; /* Vermelho mais escuro no hover */
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  /* Opcional: estilo para quando o carrinho já estiver vazio */
  &:disabled {
    color: #ccc;
    cursor: not-allowed;
    background: transparent;
  }
`;

export const CartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border);

  h3 {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--text-main);
    margin: 0;

    svg {
      color: var(--primary);
    }
  }

  /* Ajuste para telas muito pequenas */
  @media (max-width: 350px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;

    .clear {
      align-self: flex-end;
    }
  }
`;